// scheduling/pages/Notifications.jsx
// Full notification feed for the logged-in staff member.

import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { fetchStaff, fetchNotifications } from '../services/schedData';
import { markNotifRead, markAllNotifsRead } from '../services/schedApi';
import { useUrlState } from '../../hooks/useUrlState';

const TYPE_CONFIG = {
  assignment:  { color: '#3B82F6', borderColor: '#BFDBFE', icon: '\uD83D\uDCC5', label: 'Assignment' },
  move:        { color: '#F59E0B', borderColor: '#FDE68A', icon: '\u21C4', label: 'Moved' },
  removal:     { color: '#EF4444', borderColor: '#FECACA', icon: '\u2716', label: 'Removed' },
  conflict:    { color: '#EF4444', borderColor: '#FECACA', icon: '\u26A0\uFE0F', label: 'Conflict' },
  dependency:  { color: '#7C3AED', borderColor: '#DDD6FE', icon: '\uD83D\uDD17', label: 'Dependency' },
  budget:      { color: '#D97706', borderColor: '#FDE68A', icon: '\uD83D\uDCB0', label: 'Budget' },
  due_date:    { color: '#DC2626', borderColor: '#FECACA', icon: '\u23F0', label: 'Due Date' },
  pto_approved:  { color: '#F59E0B', borderColor: '#FDE68A', icon: '\uD83C\uDFD6\uFE0F', label: 'PTO' },
  pto_rejected:  { color: '#F59E0B', borderColor: '#FDE68A', icon: '\uD83C\uDFD6\uFE0F', label: 'PTO' },
  pto_cancelled: { color: '#F59E0B', borderColor: '#FDE68A', icon: '\uD83C\uDFD6\uFE0F', label: 'PTO' },
  client_stop_work:   { color: '#D97706', borderColor: '#FDE68A', icon: '\u26A0\uFE0F', label: 'Stop Work' },
  client_resume_work: { color: '#059669', borderColor: '#A7F3D0', icon: '\u2705',       label: 'Resume Work' },
  client_lost:        { color: '#DC2626', borderColor: '#FECACA', icon: '\u274C',       label: 'Client Lost' },
  bug_resolved:       { color: '#6CC24A', borderColor: '#C7E9B4', icon: '\uD83D\uDC1B', label: 'Bug Resolved' },
};

export default function Notifications() {
  const { ready, email, username } = useAuth();
  const navigate = useNavigate();
  const { refreshNotifications } = useOutletContext() || {};

  const [myStaffId, setMyStaffId] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  // Filter lives in the URL (?filter=) for shareable / navbot deeplinks.
  const [filter, setFilter] = useUrlState('filter', 'all', { validate: ['all', 'unread'] }); // 'all' | 'unread'

  useEffect(() => {
    if (!ready) return;
    fetchStaff().then(staffData => {
      const active = (staffData || []).filter(s => s.IsActive);
      let me = email && active.find(s => (s.Email || '').toLowerCase() === email.toLowerCase());
      if (!me) {
        const prefix = (email || username || '').split('@')[0].toLowerCase();
        if (prefix) me = active.find(s => (s.Email || '').toLowerCase().startsWith(prefix + '@'));
      }
      if (me) setMyStaffId(me.StaffId);
    }).catch(() => {});
  }, [ready, email, username]);

  useEffect(() => {
    if (!myStaffId) return;
    setLoading(true);
    fetchNotifications(myStaffId)
      .then(data => setNotifications(data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [myStaffId]);

  const filtered = useMemo(() => {
    if (filter === 'unread') return notifications.filter(n => !n.IsRead);
    return notifications;
  }, [notifications, filter]);

  const unreadCount = notifications.filter(n => !n.IsRead).length;

  const handleMarkRead = async (notifId) => {
    await markNotifRead(notifId);
    setNotifications(prev => prev.map(n => n.NotifId === notifId ? { ...n, IsRead: true, ReadAt: new Date().toISOString() } : n));
    refreshNotifications?.();
  };

  const handleMarkAllRead = async () => {
    if (!myStaffId) return;
    await markAllNotifsRead(myStaffId);
    setNotifications(prev => prev.map(n => ({ ...n, IsRead: true })));
    refreshNotifications?.();
  };

  const [expandedIds, setExpandedIds] = useState(new Set());
  const toggleExpand = (id) => setExpandedIds(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });

  const fmtD = (d) => new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  const parseMeta = (n) => {
    if (!n.Metadata) return null;
    try { return typeof n.Metadata === 'string' ? JSON.parse(n.Metadata) : n.Metadata; } catch { return null; }
  };

  const renderChangeDetail = (c) => {
    switch (c.type) {
      case 'hours': return <><span style={{ color: '#9CA3AF' }}>{fmtD(c.date)}</span> {c.oldHours}h &rarr; {c.newHours}h</>;
      case 'moved': return <><span style={{ color: '#9CA3AF' }}>{fmtD(c.oldDate)}</span> &rarr; {fmtD(c.newDate)} ({c.hours}h)</>;
      case 'added': return <><span style={{ color: '#9CA3AF' }}>{fmtD(c.date)}</span> <span style={{ color: '#059669' }}>Added</span> ({c.hours}h){c.note ? <span style={{ color: '#6B7280' }}> {c.note}</span> : null}</>;
      case 'removed': return <><span style={{ color: '#9CA3AF' }}>{fmtD(c.date)}</span> <span style={{ color: '#DC2626' }}>Removed</span> ({c.hours}h)</>;
      case 'reassign': return <><span style={{ color: '#9CA3AF' }}>{fmtD(c.date)}</span> Reassigned to <span style={{ fontWeight: 600 }}>{c.staffName}</span></>;
      case 'job_change': return <><span style={{ color: '#9CA3AF' }}>{fmtD(c.date)}</span> Moved to different job</>;
      default: return <>{JSON.stringify(c)}</>;
    }
  };

  if (loading) return <div className="sched-home-loading"><div className="sched-home-loading-text">Loading notifications...</div></div>;

  return (
    <div className="sched-notif-page">
      <div className="sched-notif-page-header">
        <div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#111827' }}>Notifications</h2>
          <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
            {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 2 }}>
            <button className={`sched-mgr-pill${filter === 'all' ? ' sched-mgr-pill--active' : ''}`}
              onClick={() => setFilter('all')}>All ({notifications.length})</button>
            <button className={`sched-mgr-pill${filter === 'unread' ? ' sched-mgr-pill--active' : ''}`}
              onClick={() => setFilter('unread')}>Unread ({unreadCount})</button>
          </div>
          {unreadCount > 0 && (
            <button className="sched-mgr-pill" onClick={handleMarkAllRead}>Mark All Read</button>
          )}
        </div>
      </div>

      <div className="sched-notif-page-list">
        {filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF' }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>{filter === 'unread' ? '\u2705' : '\uD83D\uDD14'}</div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>
              {filter === 'unread' ? 'No unread notifications' : 'No notifications yet'}
            </div>
          </div>
        )}
        {filtered.map(n => {
          const cfg = TYPE_CONFIG[n.NotifType] || TYPE_CONFIG.assignment;
          const meta = parseMeta(n);
          const hasDetails = meta?.changes?.length > 0;
          const isExpanded = expandedIds.has(n.NotifId);
          return (
            <div key={n.NotifId}
              className={`sched-notif-page-item${n.IsRead ? '' : ' sched-notif-page-item--unread'}`}
              style={{ borderLeftColor: cfg.color }}
              onClick={() => { if (!n.IsRead) handleMarkRead(n.NotifId); }}
            >
              <div className="sched-notif-page-icon" style={{ color: cfg.color }}>{cfg.icon}</div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{n.Title}</span>
                  <span style={{ fontSize: 10, fontWeight: 600, color: cfg.color, background: cfg.borderColor, padding: '1px 6px', borderRadius: 4 }}>{cfg.label}</span>
                </div>
                <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 4, whiteSpace: 'pre-line' }}>{n.Body}</div>

                {/* Expandable details */}
                {hasDetails && (
                  <div style={{ marginBottom: 4 }}>
                    <button onClick={(e) => { e.stopPropagation(); toggleExpand(n.NotifId); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: '#3B82F6', fontWeight: 600, padding: 0, textDecoration: 'underline' }}>
                      {isExpanded ? 'Hide details' : `Show details (${meta.changes.length})`}
                    </button>
                    {isExpanded && (
                      <div style={{ marginTop: 6, padding: '6px 0', borderTop: '1px solid #F3F4F6' }}>
                        {meta.changes.map((c, i) => (
                          <div key={i} style={{ fontSize: 11, color: '#374151', padding: '2px 0', display: 'flex', gap: 6 }}>
                            {renderChangeDetail(c)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div style={{ fontSize: 11, color: '#9CA3AF' }}>
                  {n.ActorName && <>{n.ActorName} &middot; </>}
                  {new Date(n.CreatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                </div>
              </div>
              {!n.IsRead && <div className="sched-notif-item-unread-dot" />}
              {n.RelatedJobId && (
                <button className="sched-sug-back" style={{ fontSize: 11, padding: '4px 8px', alignSelf: 'flex-start', marginTop: 4 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    const staffIds = meta?.affectedStaffIds?.length > 0
                      ? [...new Set(meta.affectedStaffIds)]
                      : [...new Set([n.RecipientId, n.RelatedStaffId].filter(Boolean))];
                    sessionStorage.setItem('gridViewContext', JSON.stringify({
                      staffIds,
                      oldCells: meta?.oldCells || [],
                      newCells: meta?.newCells || [],
                      message: `Showing: ${n.Title}`,
                    }));
                    navigate('/schedule/grid');
                  }}>
                  View
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
